/*!
 * Copyright 2026 - Swiss Data Science Center (SDSC)
 * A partnership between École Polytechnique Fédérale de Lausanne (EPFL) and
 * Eidgenössische Technische Hochschule Zürich (ETHZ).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import cx from "classnames";
import { startCase } from "lodash-es";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowRepeat, FileEarmarkArrowDown } from "react-bootstrap-icons";
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";

import { ErrorAlert } from "~/components/Alert";
import { Loader } from "~/components/Loader";
import ScrollableModal from "~/components/modal/ScrollableModal";
import useRenkuToast from "~/components/toast/useRenkuToast";
import { generateZip } from "~/utils/helpers/HelperFunctions";
import { getSessionStatusStyles } from "../sessionsV2/components/SessionStatus/SessionStatus";
import type { SessionV2 } from "../sessionsV2/sessionsV2.types";
import ColoredLogs from "./ColoredLogs";

interface LogsQuery {
  data?: Record<string, string | undefined> | undefined;
  error?: FetchBaseQueryError | SerializedError | undefined;
  isFetching: boolean;
  isLoading: boolean;
  refetch: () => Promise<{
    data?: Record<string, string | undefined> | undefined;
    error?: FetchBaseQueryError | SerializedError | undefined;
  }>;
}

type DownloadLogsLazyQueryTrigger = () => Promise<{
  data?: Record<string, string | undefined> | undefined;
  error?: FetchBaseQueryError | SerializedError | undefined;
}> & { unsubscribe: () => void };

interface LogsModalModalProps {
  isOpen: boolean;
  name: string;
  query: LogsQuery;
  downloadQueryTrigger?: DownloadLogsLazyQueryTrigger;
  sessionError?: string;
  sessionState?: SessionV2["status"]["state"];
  title: ReactNode;
  toggle: () => void;
  defaultTab?: string | ((tab: string) => boolean);
  emptyMessage?: ReactNode;
  tabLabel?: (tab: string) => string;
}

export default function LogsModal({
  isOpen,
  name,
  query,
  downloadQueryTrigger,
  sessionError,
  sessionState,
  title,
  toggle,
  defaultTab,
  emptyMessage,
  tabLabel,
}: LogsModalModalProps) {
  const { data, error, isLoading, isFetching, refetch } = query;

  return (
    <ScrollableModal className="modal-xl" isOpen={isOpen} toggle={toggle}>
      <ModalHeader className="header-multiline" toggle={toggle} tag="div">
        <h2 className={cx(!sessionState && "mb-0")}>{title}</h2>
        {sessionState && (
          <h3
            className={cx(
              "fs-4",
              "fst-italic",
              "mb-0",
              getSessionStatusStyles(
                {
                  status: { state: sessionState },
                  image: "url",
                },
                "session",
              )["textColorCard"],
            )}
          >
            Session status: {sessionState}
          </h3>
        )}
      </ModalHeader>
      <ModalBody className={cx("d-flex", "flex-column", "h-auto")}>
        {sessionError && <ErrorAlert>{sessionError}</ErrorAlert>}
        <LogsModalBody
          data={data}
          error={error}
          isFetching={isFetching}
          isLoading={isLoading}
          refetch={refetch}
          defaultTab={defaultTab}
          emptyMessage={emptyMessage}
          sessionState={sessionState}
          tabLabel={tabLabel}
        />
      </ModalBody>
      <ModalFooter>
        <ModalFooterButtons
          data={data}
          isFetching={isFetching}
          name={name}
          refetch={refetch}
          downloadQueryTrigger={downloadQueryTrigger}
        />
      </ModalFooter>
    </ScrollableModal>
  );
}

type LogsModalBodyProps = LogsQuery &
  Pick<
    LogsModalModalProps,
    "defaultTab" | "emptyMessage" | "sessionState" | "tabLabel"
  >;

export function LogsModalBody({
  data,
  error,
  isFetching,
  isLoading,
  refetch,
  defaultTab,
  emptyMessage,
  sessionState,
  tabLabel,
}: LogsModalBodyProps) {
  if (isLoading) {
    return <Loader />;
  }

  if (error || data == null) {
    return (
      <p data-cy="logs-unavailable-message" className="mb-0">
        Logs unavailable.{" "}
        {(sessionState === "succeeded" || sessionState === "failed") && (
          <>Note that old logs are regularly purged. </>
        )}
        Please try to{" "}
        <Button
          color="primary"
          onClick={refetch}
          size="sm"
          disabled={isFetching}
        >
          refresh
        </Button>{" "}
        them again.
      </p>
    );
  }

  if (Object.keys(data).length < 1) {
    return (
      <NoLogsAvailable
        refetch={refetch}
        emptyMessage={emptyMessage}
        sessionState={sessionState}
      />
    );
  }

  return <TabbedLogs data={data} defaultTab={defaultTab} tabLabel={tabLabel} />;
}

type NoLogsAvailableProps = Pick<LogsQuery, "refetch"> &
  Pick<LogsModalModalProps, "emptyMessage" | "sessionState">;

function NoLogsAvailable({
  refetch,
  emptyMessage,
  sessionState,
}: NoLogsAvailableProps) {
  if (sessionState === "succeeded" || sessionState === "failed") {
    return (
      <p data-cy="no-logs-message">
        No logs available. Note that old logs are regularly purged.
      </p>
    );
  }
  return (
    <>
      <p data-cy="no-logs-message">
        {emptyMessage ?? "No logs available for this pod yet."}
      </p>
      <p>
        You can try to{" "}
        <Button
          color="primary"
          data-cy="retry-logs-body"
          size="sm"
          onClick={refetch}
        >
          refresh
        </Button>{" "}
        them after a while.
      </p>
    </>
  );
}

type TabbedLogsProps = {
  data: Exclude<LogsQuery["data"], undefined>;
} & Pick<LogsModalModalProps, "defaultTab" | "tabLabel">;

function TabbedLogs({ data, defaultTab, tabLabel }: TabbedLogsProps) {
  const sortedLogs = useMemo(() => {
    const isDefaultTab =
      typeof defaultTab === "function"
        ? defaultTab
        : (tab: string) => tab === defaultTab;
    const result: LogTab[] = [];
    const keys = Object.keys(data);
    const defaultKey = keys.find(isDefaultTab);
    if (defaultKey != null) {
      result.push({ tab: defaultKey, content: data[defaultKey] ?? "" });
    }
    for (const key of keys) {
      if (key !== defaultKey) {
        result.push({ tab: key, content: data[key] ?? "" });
      }
    }
    return result;
  }, [data, defaultTab]);

  const [activeTab, setActiveTab] = useState<string>(
    sortedLogs.at(0)?.tab ?? "",
  );

  const preRef = useRef<HTMLPreElement>(null);

  // Scrolls to the bottom of the logs when we change tabs
  useEffect(() => {
    if (preRef.current && activeTab) {
      const pre = preRef.current;
      requestAnimationFrame(() => {
        pre.scrollTop = pre.scrollHeight;
      });
    }
  }, [activeTab]);

  // Reset the active tab if the container name has disappeared
  useEffect(() => {
    if (sortedLogs.every(({ tab }) => tab !== activeTab)) {
      // TODO: fix react-hooks/set-state-in-effect
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveTab(sortedLogs.at(0)?.tab ?? "");
    }
  }, [activeTab, sortedLogs]);

  return (
    <>
      <Nav className={cx("mb-2", "position-sticky", "top-0")} tabs>
        {sortedLogs.map(({ tab }) => (
          <NavItem key={tab} data-cy="log-tab" role="button">
            <NavLink
              className={cx(activeTab === tab && "active")}
              onClick={() => {
                setActiveTab(tab);
              }}
            >
              {tabLabel ? tabLabel(tab) : startCase(tab)}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      <TabContent
        activeTab={activeTab}
        className={cx("flex-1", "overflow-auto")}
      >
        {sortedLogs.map(({ tab, content }) => (
          <TabPane key={tab} tabId={tab}>
            <div className={cx("d-flex", "flex-column")}>
              <pre
                className="overflow-visible"
                // eslint-disable-next-line spellcheck/spell-checker
                style={{ whiteSpace: "pre-line", maxHeight: "60vh" }}
                ref={activeTab === tab ? preRef : undefined}
              >
                {content ? (
                  <ColoredLogs>{content}</ColoredLogs>
                ) : (
                  <i>Empty log file</i>
                )}
              </pre>
            </div>
          </TabPane>
        ))}
      </TabContent>
    </>
  );
}

interface LogTab {
  tab: string;
  content: string;
}

type ModalFooterButtonsProps = Pick<
  LogsQuery,
  "data" | "isFetching" | "refetch"
> &
  Pick<LogsModalModalProps, "name" | "downloadQueryTrigger">;

function ModalFooterButtons({
  data,
  isFetching,
  name,
  refetch,
  downloadQueryTrigger,
}: ModalFooterButtonsProps) {
  const [isDownloading, triggerDownload] = useDownloadLogs(
    name,
    refetch,
    downloadQueryTrigger,
  );
  const canDownload =
    !isFetching &&
    !isDownloading &&
    data != null &&
    Object.keys(data).length >= 1;

  return (
    <>
      <Button
        color="outline-primary"
        id="session-refresh-logs"
        onClick={refetch}
        disabled={isFetching}
      >
        {isFetching ? (
          <Loader className="me-1" inline size={16} />
        ) : (
          <ArrowRepeat className={cx("bi", "me-1")} />
        )}
        Refresh logs
      </Button>

      <Button
        data-cy="session-log-download-button"
        color="outline-primary"
        onClick={triggerDownload}
        disabled={!canDownload}
      >
        {isDownloading ? (
          <Loader className="me-1" inline size={16} />
        ) : (
          <FileEarmarkArrowDown className={cx("bi", "me-1")} />
        )}
        {isDownloading ? "Downloading" : "Download"}
      </Button>
    </>
  );
}

/**
 * Hook to download the logs as an archive
 *
 * NOTE: will download with maxLines = 250, so the logs will be incomplete
 */
export function useDownloadLogs(
  name: string,
  refetch: LogsQuery["refetch"],
  downloadQueryTrigger: DownloadLogsLazyQueryTrigger | undefined | null,
): [boolean, () => void] {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { renkuToastSuccess, renkuToastDanger } = useRenkuToast();

  const triggerDownload = useCallback(() => {
    let ignore: boolean = false;
    setIsDownloading(true);
    const trigger: () => Promise<{
      data?: Record<string, string | undefined> | undefined;
      error?: FetchBaseQueryError | SerializedError | undefined;
    }> & { unsubscribe?: () => void } = downloadQueryTrigger ?? refetch;
    const promise = trigger();
    const unsubscribe = promise.unsubscribe;
    promise
      .then(async ({ data, error }) => {
        if (error != null || data == null) {
          renkuToastDanger({ textHeader: "Failed to download logs" });
          return;
        }
        const logName = `Logs_${name}`;
        const files: { name: string; content: Blob }[] = [];
        for (const key in data) {
          const contents = data[key] ?? "";
          // create the blob element to download logs as a file
          const file = new Blob([contents], { type: "text/plain" });
          files.push({
            name: `${logName}/${key}.txt`,
            content: file,
          });
        }
        await generateZip(files, logName);
        renkuToastSuccess({ textHeader: "Successfully downloaded logs" });
      })
      .catch(() => {
        renkuToastDanger({ textHeader: "Failed to download logs" });
      })
      .finally(() => {
        if (unsubscribe != null) {
          unsubscribe();
        }
        if (!ignore) {
          setIsDownloading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [
    downloadQueryTrigger,
    name,
    refetch,
    renkuToastDanger,
    renkuToastSuccess,
  ]);

  return [isDownloading, triggerDownload];
}
